import { Document, Page, Text, View } from '@react-pdf/renderer'
import { PdfPreparedFor, tw } from '~/components/pdfs'
import {
  getFilteredTransactions,
  type Listing,
  type ListingTransaction,
} from '~/models'
import { cn, fmtNumberAbbr } from '~/utils'
import {
  CompChart,
  CompStats,
  CompTable,
  PdfBrokerFooter,
  type CompChartConfig,
  type CompStatsConfig,
  type CompTableConfig,
  type TableColumnConfig,
} from './components'
import { PdfDisclaimersPage, ReportPdfTitlePage } from './pages'
import type { ReportPdfSwitchProps } from './ReportPdfSwitch'
import {
  getLeaseCompsStatsData,
  getLeaseCompsTableData,
  getLeaseTransactionsData,
} from './utils'

// Leased Comps Chart Configuration Function
const getLeasedChartConfig = (): CompChartConfig => {
  return {
    bars: [
      {
        dataKey: 'leasedRate',
        fill: '#a78bfa',
        name: 'Leased Rate SF/Yr',
      },
      {
        dataKey: 'listedRate',
        fill: '#818cf8',
        name: 'Listed Rate SF/Yr',
      },
    ],
    yAxisWidth: 70,
    yAxisFormatter: (value: number) => `$${value}/SF/yr`,
  }
}

// Leased Comps Stats Configuration Function
const getLeasedStatsConfig = (isLandReport: boolean): CompStatsConfig[] => {
  const statsConfig: CompStatsConfig[] = [
    {
      key: 'averageLeasedRate',
      label: 'Avg. Leased Rate',
      formatter: (value: number) =>
        `$${(Math.round(value * 100) / 100).toLocaleString()}/SF/yr`,
    },
    {
      key: 'averageListedRate',
      label: 'Avg. Listed Rate',
      formatter: (value: number) =>
        `$${(Math.round(value * 100) / 100).toLocaleString()}/SF/yr`,
    },
    {
      key: 'medianLeaseRate',
      label: 'Median Lease Rate',
      formatter: (value: number) =>
        `$${(Math.round(value * 100) / 100).toLocaleString()}/SF/yr`,
    },
  ]

  if (isLandReport) {
    statsConfig.push({
      key: 'averageAcres',
      label: 'Avg. Acres',
      formatter: (value: number) =>
        value > 100
          ? `${Math.round(value).toLocaleString()} ac`
          : `${value.toFixed(2)} ac`,
      secondaryFormatter: (value: number) =>
        `${fmtNumberAbbr(value * 43560)} SF`,
    })
  } else {
    statsConfig.push({
      key: 'averageAvailableSf',
      label: 'Avg. Available SF',
      formatter: (value: number) => `${Math.round(value).toLocaleString()} SF`,
    })
  }

  return statsConfig
}

// Leased Comps Table Configuration Function
const getLeasedTableConfig = (isLandReport: boolean): CompTableConfig => {
  const columns: TableColumnConfig[] = [
    {
      key: 'address',
      label: 'Address',
    },
    {
      key: 'spaceName',
      label: 'Space Name',
    },
    {
      key: 'leasedRate',
      label: 'Leased Rate',
      formatter: (value?: number) =>
        value ? `$${value.toLocaleString()}/SF/yr` : '-',
    },
    {
      key: 'listedRate',
      label: 'Listed Rate',
      formatter: (value?: number) =>
        value ? `$${value.toLocaleString()}/SF/yr` : '-',
    },
  ]
  if (isLandReport) {
    columns.push(
      {
        key: 'acres',
        label: 'Acres',
        formatter: (value?: number) =>
          value ? `${value.toLocaleString()} ac` : '-',
      },
      {
        key: 'lotSizeSf',
        label: 'Lot Size SF',
        formatter: (value?: number) =>
          value ? `${value.toLocaleString()} SF` : '-',
      }
    )
  } else {
    columns.push({
      key: 'availableSf',
      label: 'Available SF',
      formatter: (value?: number) =>
        value ? `${value.toLocaleString()} SF` : '-',
    })
  }
  columns.push({
    key: 'date',
    label: 'Date',
  })
  return {
    columns,
  }
}

export default function ReportLeasedCompsPdf({
  organization,
  filters,
  title,
  user,
  company,
  brokers,
  listings,
  pdfOptions,
}: ReportPdfSwitchProps) {
  const leasedTransactions: (ListingTransaction & {
    listing: Listing
  })[] = listings
    .map((listing) => {
      const filteredTransactions = getFilteredTransactions(
        filters,
        listing.transactions ?? []
      )
      return filteredTransactions.map((transaction) => ({
        ...transaction,
        listing,
      }))
    })
    .flat()
    .filter((t) => t.lease)

  leasedTransactions.sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime()
  })

  const isLandReport =
    leasedTransactions.filter((t) => t.listing.propertyTypes[0] === 'Land')
      .length >=
    leasedTransactions.length - Math.round(leasedTransactions.length / 4) // threshold?

  const statsData = getLeaseCompsStatsData(leasedTransactions, isLandReport)
  const leasedData = getLeaseTransactionsData(
    leasedTransactions,
    isLandReport,
    false
  )
  const chartData = getLeaseCompsTableData(leasedData, isLandReport)

  return (
    <Document title={title}>
      {pdfOptions.showTitlePage && (
        <ReportPdfTitlePage
          title={title}
          company={company}
          user={user}
          listings={listings}
          pdfOptions={pdfOptions}
          orientation="portrait"
        />
      )}
      {leasedTransactions.length > 0 && (
        <Page
          size="LETTER"
          style={tw('flex flex-col gap-4 px-8 pb-24 pt-8 font-sans text-sm')}
          orientation="portrait">
          <View
            style={tw(
              cn(
                !pdfOptions.showTitlePage &&
                  'flex flex-row justify-between gap-6'
              )
            )}>
            {!pdfOptions.showTitlePage && (
              <View style={tw('mb-3')}>
                <Text style={tw('font-heading mb-2 text-2xl leading-tight')}>
                  {title}
                </Text>
                <PdfPreparedFor />
              </View>
            )}

            <View style={tw(cn(!pdfOptions.showTitlePage && 'text-right'))}>
              <Text style={tw('text-xl font-bold')}>
                {leasedTransactions.length} Leased Transactions
              </Text>
            </View>
          </View>
          <CompChart data={chartData} config={getLeasedChartConfig()} />

          <CompStats
            statsData={statsData}
            config={getLeasedStatsConfig(isLandReport)}
          />

          <CompTable
            data={leasedData}
            config={getLeasedTableConfig(isLandReport)}
          />

          <PdfBrokerFooter
            organization={organization}
            user={user}
            company={company}
            brokers={brokers}
          />
        </Page>
      )}
      <PdfDisclaimersPage
        pdfOptions={pdfOptions}
        organization={organization}
        orientation="portrait"
      />
    </Document>
  )
}
